import '@ids/styles/css/styles.css';
// IDS v2 brand fonts (Itau Text Pro / Itau Display Pro). The theme tokens
// reference these families, but the @font-face declarations ship separately in
// @ids/tokens. Importing them here lets Vite bundle and self-host the woff2/ttf
// files from the app origin, fixing the broken/fallback fonts.
import '@ids/tokens/fonts/itautextpro.css';
import '@ids/tokens/fonts/itaudisplaypro.css';
import '@ids/web/icon';
import '@ids/web/list';
import '@ids/web/list/internal/list-item';
import { iDSConfig } from '@ids/web/tools';

import './styles/main.css';

import type { Entry } from '@/types/contentstack';
import { getEntryByUrl } from '@services/contentstack';
import { analyticsService } from '@utils/analytics';
import { initDatadog, logError } from '@utils/datadog';
import { changeSegment } from '@utils/segment-manager';

import { renderHeader, type HeaderParams } from '@components/header';
import { renderFooter } from '@components/footer';
import {
  renderSocialLinks,
  attachSocialLinksListeners,
  type SocialLinkParams,
} from '@components/social-links';
import {
  renderModuleLinks,
  attachModuleLinksListeners,
  type ModuleBlock,
} from '@components/module-links';
import { renderNotFound } from '@components/not-found';

const DEFAULT_SOCIAL_TITLE = 'Acesse nossas redes sociais';
const ROUTE_BASE_SEGMENT = 'social-link';

function getRouteName(): string {
  const segments = window.location.pathname.split('/').filter(Boolean);
  if (segments[0] === ROUTE_BASE_SEGMENT) {
    segments.shift();
  }
  return segments[0] ?? '';
}

function getApp(): HTMLElement {
  const app = document.getElementById('app');
  if (!app) {
    throw new Error('Root element #app not found');
  }
  return app;
}

function toHeaderParams(entry: Entry): HeaderParams {
  return {
    title: entry.title,
    page_title: entry.page_title,
    logo: {
      path: entry.header.logo.image.path,
      alt: entry.header.logo.alt,
    },
    background: {
      effect: entry.header.header_style.background_effect,
      color: entry.header.header_style.custom_background,
    },
  };
}

function toSocialLinks(entry: Entry): SocialLinkParams[] {
  return entry.social_links_block.map(({ social_link }) => ({
    background: entry.social_links_background,
    type: social_link.name,
    href: social_link.link.href,
    arialLabel: social_link.link.title,
  }));
}

function toModuleBlocks(entry: Entry): ModuleBlock[] {
  return entry.content_block.map(({ section_block }) => ({
    title: section_block.title,
    icon_color: section_block.icon_color,
    items: section_block.section_links.map(({ item }) => item),
  }));
}

function applySocialLinksDefaults(entry: Entry): void {
  if (typeof entry.social_links_active === 'undefined') {
    entry.social_links_active = true;
  }
  if (typeof entry.social_links_title === 'undefined') {
    entry.social_links_title = DEFAULT_SOCIAL_TITLE;
  }
}

function renderPage(entry: Entry): void {
  const app = getApp();
  const socialTitle = entry.social_links_title ?? DEFAULT_SOCIAL_TITLE;

  const socialSection = entry.social_links_active
    ? renderSocialLinks(toSocialLinks(entry), socialTitle)
    : '';

  app.innerHTML = `
    ${renderHeader(toHeaderParams(entry))}
    <main class="ids-container" data-testid="social-link-home-main">
      ${renderModuleLinks(toModuleBlocks(entry))}
      ${socialSection}
    </main>
    ${renderFooter({ address: entry.footer.address, legal_infos: entry.footer.legal_infos })}
  `;

  attachModuleLinksListeners(app);
  if (entry.social_links_active) {
    attachSocialLinksListeners(app, socialTitle);
  }

  analyticsService.pageLoad(entry.page_title);
}

function renderNotFoundPage(title: string): void {
  getApp().innerHTML = renderNotFound();
  document.title = title;
}

async function bootstrap(): Promise<void> {
  initDatadog();

  await iDSConfig({
    theme: 'varejo',
    schemeTheme: 'light',
    environment: import.meta.env.VITE_IDS_ENV || 'prod',
  });

  const name = getRouteName();

  if (!name || name === '404') {
    renderNotFoundPage('Social Link - Página não encontrada');
    return;
  }

  try {
    const entry = await getEntryByUrl(name);
    await changeSegment(entry.segment_group?.name ?? '');
    applySocialLinksDefaults(entry);
    document.title = entry.page_title;
    renderPage(entry);
  } catch (error) {
    logError('Failed to load page entry', {
      name,
      error: error instanceof Error ? error.message : String(error),
    });
    renderNotFoundPage('Social Link - Página não encontrada');
  }
}

void bootstrap();
